import React, { useMemo, useCallback, type RefObject } from 'react';
import type { PreloadedQuery } from 'react-relay';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import { useExperienceStart } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { list_hydrateJql_Query as RuleListHydrateJqlQueryType } from '@atlassian/jira-relay/src/__generated__/list_hydrateJql_Query.graphql';
import type { list_RuleList_Query as RuleListQueryType } from '@atlassian/jira-relay/src/__generated__/list_RuleList_Query.graphql';
import { useQueryParam } from '@atlassian/react-resource-router';
import { LIST_FORMAT_RULE_EXPERIENCE } from '../../common/constants.tsx';

const FORMAT_SIDEBAR_SIDEBAR_CONTENT_KEY = 'formatRulesSidebar';

export type FormatRulesSidebarProps = {
	onCloseSidebar: () => void;
	query?: PreloadedQuery<RuleListQueryType> | null;
	projectId: string;
	hydrateJqlQuery?: PreloadedQuery<RuleListHydrateJqlQueryType> | null;
	showOnboarding?: boolean;
};

export const useFormatRulesSidebar = ({
	projectId,
	SidebarComponent,
	formatButtonRef,
}: {
	projectId: string;
	SidebarComponent: React.FC<FormatRulesSidebarProps>;
	formatButtonRef?: RefObject<HTMLButtonElement | undefined>;
}) => {
	const [, setSelectedIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);
	const [{ content }, { setSidebarContent }] = useSidebar();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isFormatRulesSidebarOpen = useMemo(
		() => content?.key === FORMAT_SIDEBAR_SIDEBAR_CONTENT_KEY,
		[content],
	);

	const startExperience = useExperienceStart(LIST_FORMAT_RULE_EXPERIENCE);

	const onCloseSidebar = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'formatRulesCloseButton');
		setSidebarContent(null);
		formatButtonRef?.current?.focus();
	}, [createAnalyticsEvent, formatButtonRef, setSidebarContent]);

	const onClickFormatButton = useCallback(
		({ showOnboarding }: { showOnboarding: boolean }) => {
			if (isFormatRulesSidebarOpen) {
				onCloseSidebar();
			} else {
				setSelectedIssueKey(undefined);
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'formatRulesSidebarButton');
				startExperience();
				setSidebarContent(
					<SidebarComponent
						key={FORMAT_SIDEBAR_SIDEBAR_CONTENT_KEY}
						onCloseSidebar={onCloseSidebar}
						projectId={projectId}
						showOnboarding={showOnboarding}
					/>,
				);
			}
		},
		[
			isFormatRulesSidebarOpen,
			onCloseSidebar,
			setSelectedIssueKey,
			createAnalyticsEvent,
			startExperience,
			setSidebarContent,
			SidebarComponent,
			projectId,
		],
	);

	return {
		onClickFormatButton,
		isFormatRulesSidebarOpen,
	};
};
